/**
 * QUIPWA — night theme
 *
 * Night mode used to be `filter: grayscale(.9) brightness(.65)` over the whole
 * app. That erased exactly what colour carries here — the 1–5 ratings, deck
 * colours, states — and dimmed images and focus rings with it.
 *
 * Now it is a real palette derived from the active theme: darker, warmer, with
 * less luminance in the text, and the semantic colours kept (and re-checked
 * for AA on the new background by deriveSecondaryColors).
 *
 * ```ts
 * const shown = isNightActive(mode, schedule, new Date()) ? nightVariant(profile) : profile
 * ```
 */
import { isLightColor, mix, readableTextColor } from './color-utils'
import type { ThemeProfile } from './theme-store'

export type NightMode = 'off' | 'on' | 'schedule'

/** Hours 0–23. `from > to` crosses midnight (21 → 7). */
export interface NightSchedule {
  from: number
  to: number
}

export const DEFAULT_NIGHT_SCHEDULE: NightSchedule = { from: 21, to: 7 }

export function isNightActive(mode: NightMode, schedule: NightSchedule, now: Date): boolean {
  if (mode === 'on') return true
  if (mode === 'off') return false
  const h = now.getHours()
  if (schedule.from === schedule.to) return false
  return schedule.from > schedule.to ? h >= schedule.from || h < schedule.to : h >= schedule.from && h < schedule.to
}

/** Ms until the next schedule boundary, so the injector re-checks exactly then. */
export function msUntilNextBoundary(schedule: NightSchedule, now: Date): number {
  const hourStart = new Date(now)
  hourStart.setMinutes(0, 0, 0)
  for (let i = 1; i <= 24; i++) {
    // Step from a fixed origin: re-applying setHours on a date that already
    // crossed midnight added a whole day per step.
    const candidate = new Date(hourStart.getTime() + i * 60 * 60 * 1000)
    const h = candidate.getHours()
    if (h === schedule.from || h === schedule.to) return candidate.getTime() - now.getTime()
  }
  return 60 * 60 * 1000
}

const EMBER = '#1A120A' // warm near-black the night background leans toward
const CANDLE_TITLE = '#EAD3B0'
const CANDLE_BODY = '#C9B394'
const WARM_ACCENT = '#D49A52'

/** The night version of a theme: darker, warmer, lower-luminance text. */
export function nightVariant(profile: ThemeProfile): ThemeProfile {
  const c = profile.colors
  const light = isLightColor(c.bgBase)
  // A light theme becomes a dark warm one; a dark one gets darker and warmer.
  const bgBase = light ? mix(EMBER, c.bgBase, 0.06) : mix(mix(c.bgBase, '#000000', 0.35), EMBER, 0.3)
  const surface = light ? mix(EMBER, c.surface, 0.12) : mix(mix(c.surface, '#000000', 0.3), EMBER, 0.3)
  const title = mix(light ? CANDLE_TITLE : c.textTitle, CANDLE_TITLE, 0.35)
  const body = mix(light ? CANDLE_BODY : c.textBody, CANDLE_BODY, 0.3)

  return {
    ...profile,
    colors: {
      primary: mix(c.primary, WARM_ACCENT, 0.25),
      bgBase,
      surface,
      // Dimmer than the day theme, but never below AA.
      textTitle: readableTextColor(mix(title, bgBase, 0.12), bgBase),
      textBody: readableTextColor(mix(body, bgBase, 0.1), bgBase),
    },
    background: {
      ...profile.background,
      value: profile.background.type === 'image' ? profile.background.value : bgBase,
      opacity: profile.background.type === 'image' ? Math.min(profile.background.opacity, 0.45) : profile.background.opacity,
    },
  }
}
